import { createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import { convertFieldToString, buildApiV3Url } from "../shared/Utils";

const MATRIX_PAGE_SIZE = 100;

export const exemptionMatrixSlice = createSlice({
    name: "exemptionMatrix",
    initialState: {
        isLoadingMatrixExposureZones: false,
        isLoadingMatrixExemptReasons: false,
        isLoadingMoreMatrixExemptReasons: false,
        isLoadingData: false,
        data: [],
        matrixExposureZoneData: [],
        matrixExemptReasonData: [],
        matrixExemptReasonSkip: 0,
        matrixExemptReasonTotalCount: 0,
    },

    reducers: {
        setData: (state, action) => {
            state.data = action.payload;
        },
        setLoadingData: (state, action) => {
            state.isLoadingData = action.payload;
        },
        setMatrixExposureZoneData: (state, action) => {
            state.matrixExposureZoneData = action.payload;
        },
        setLoadingMatrixExposureZones: (state, action) => {
            state.isLoadingMatrixExposureZones = action.payload;
        },
        setMatrixExemptReasonData: (state, action) => {
            state.matrixExemptReasonData = action.payload;
        },
        appendMatrixExemptReasonData: (state, action) => {
            state.matrixExemptReasonData = [...state.matrixExemptReasonData, ...action.payload];
        },
        setLoadingMatrixExemptReasons: (state, action) => {
            state.isLoadingMatrixExemptReasons = action.payload;
        },
        setLoadingMoreMatrixExemptReasons: (state, action) => {
            state.isLoadingMoreMatrixExemptReasons = action.payload;
        },
        setMatrixExemptReasonSkip: (state, action) => {
            state.matrixExemptReasonSkip = action.payload;
        },
        setMatrixExemptReasonTotalCount: (state, action) => {
            state.matrixExemptReasonTotalCount = action.payload;
        },
    },
});

export const {
    setData,
    setLoadingData,
    setMatrixExposureZoneData,
    setLoadingMatrixExposureZones,
    setMatrixExemptReasonData,
    appendMatrixExemptReasonData,
    setLoadingMatrixExemptReasons,
    setLoadingMoreMatrixExemptReasons,
    setMatrixExemptReasonSkip,
    setMatrixExemptReasonTotalCount,
} = exemptionMatrixSlice.actions;

export const selectData = state => {
    return state.exemptionMatrix.data;
};

export const selectIsLoadingData = state => {
    return state.exemptionMatrix.isLoadingData;
};

export const selectMatrixExposureZoneData = state => {
    return state.exemptionMatrix.matrixExposureZoneData;
};

export const selectMatrixExemptReasonData = state => {
    return state.exemptionMatrix.matrixExemptReasonData;
};

export const selectIsLoadingMatrixExposureZones = state => {
    return state.exemptionMatrix.isLoadingMatrixExposureZones;
};

export const selectIsLoadingMatrixExemptReasons = state => {
    return state.exemptionMatrix.isLoadingMatrixExemptReasons;
};

export const selectIsLoadingMoreMatrixExemptReasons = state => {
    return state.exemptionMatrix.isLoadingMoreMatrixExemptReasons;
};

export const selectHasMoreMatrixExemptReasons = state => {
    const { matrixExemptReasonData, matrixExemptReasonTotalCount } = state.exemptionMatrix;
    return matrixExemptReasonData.length < matrixExemptReasonTotalCount;
};

export const fetchExpirationMatrixExposureZones = () => async (dispatch, getState) => {
    const { isLoadingExposureZones } = getState().exemptionMatrix;

    if (isLoadingExposureZones) {
        return;
    }

    dispatch(setLoadingMatrixExposureZones(true));

    const response = await axios.get(buildApiV3Url(`exemption-matrix/exposure-zones`), {
        withCredentials: true,
    });

    if (response.data) {
        const transformedMatrixExposureZones = convertFieldToString(response.data, null, [
            "region",
        ]);
        dispatch(setMatrixExposureZoneData(transformedMatrixExposureZones));
    }

    dispatch(setLoadingMatrixExposureZones(false));
};

export const fetchExpirationMatrixExemptReasons = exposureZoneId => async (dispatch, getState) => {
    const { isLoadingMatrixExemptReasons } = getState().exemptionMatrix;

    if (isLoadingMatrixExemptReasons) {
        return;
    }

    dispatch(setLoadingMatrixExemptReasons(true));

    const response = await axios.post(
        buildApiV3Url(`exemption-matrix/exempt-reasons`),
        { exposureZoneIds: [exposureZoneId] },
        { withCredentials: true }
    );

    if (response.data) {
        const transformedMatrixExemptReasons = convertFieldToString(response.data, null, [
            "exemptReason",
        ]);
        dispatch(setMatrixExemptReasonData(transformedMatrixExemptReasons));
    }

    dispatch(setLoadingMatrixExemptReasons(false));
};

export const fetchExemptionMatrix = filterString => async (dispatch, getState) => {
    const { isLoadingMatrixExemptReasons } = getState().exemptionMatrix;

    if (isLoadingMatrixExemptReasons) {
        return;
    }

    dispatch(setLoadingMatrixExemptReasons(true));

    const response = await axios.get(buildApiV3Url(`exemption-matrix${filterString}`), {
        withCredentials: true,
    });

    if (response.data) {
        dispatch(setData(response.data.value));
    }

    dispatch(setLoadingMatrixExemptReasons(false));
};

export const fetchCodesByManyRegions = exposureZoneId => async (dispatch, getState) => {
    const { isLoadingMatrixExemptReasons } = getState().exemptionMatrix;

    if (isLoadingMatrixExemptReasons) {
        return;
    }

    dispatch(setLoadingMatrixExemptReasons(true));
    dispatch(setMatrixExemptReasonSkip(0));
    dispatch(setMatrixExemptReasonTotalCount(0));

    const response = await axios.get(
        buildApiV3Url(
            `exemption-matrix?$filter=Region.Id IN (${exposureZoneId})&$top=${MATRIX_PAGE_SIZE}&$skip=0`
        ),
        { withCredentials: true }
    );

    if (response.data?.value) {
        dispatch(setMatrixExemptReasonData(response.data.value));
        dispatch(
            setMatrixExemptReasonTotalCount(response.data.count ?? response.data.value.length)
        );
        dispatch(setMatrixExemptReasonSkip(MATRIX_PAGE_SIZE));
    }

    dispatch(setLoadingMatrixExemptReasons(false));
};

export const fetchMoreCodesByManyRegions = exposureZoneId => async (dispatch, getState) => {
    const {
        isLoadingMoreMatrixExemptReasons,
        matrixExemptReasonSkip,
        matrixExemptReasonData,
        matrixExemptReasonTotalCount,
    } = getState().exemptionMatrix;

    if (
        isLoadingMoreMatrixExemptReasons ||
        matrixExemptReasonData.length >= matrixExemptReasonTotalCount
    ) {
        return;
    }

    dispatch(setLoadingMoreMatrixExemptReasons(true));

    const response = await axios.get(
        buildApiV3Url(
            `exemption-matrix?$filter=Region.Id IN (${exposureZoneId})&$top=${MATRIX_PAGE_SIZE}&$skip=${matrixExemptReasonSkip}`
        ),
        { withCredentials: true }
    );

    if (response.data?.value) {
        dispatch(appendMatrixExemptReasonData(response.data.value));
        dispatch(setMatrixExemptReasonSkip(matrixExemptReasonSkip + MATRIX_PAGE_SIZE));
    }

    dispatch(setLoadingMoreMatrixExemptReasons(false));
};

export default exemptionMatrixSlice.reducer;
